import { useMutation, useQuery } from "@apollo/client/react"
import { Link, useNavigate, useParams } from "react-router-dom"
import { toast } from "sonner"
import { gql } from "~/__generated__"
import { JourneyLogDisplay } from "~/__generated__/graphql"
import { useViewer } from "~/auth/use-viewer"
import { directImageUpload } from "~/common/direct-image-upload"
import { useDocumentTitle } from "~/common/use-document-title"
import { Card, CardContent } from "~/ui/card"
import { GraphqlError } from "~/ui/graphql-error"
import { ScreenLoading } from "~/ui/screen-loading"
import { JourneyLogForm } from "./journey-log-form"

const puzzleQuery = gql(/* GraphQL */ `
  query LogJourneyPuzzle($wpId: String!) {
    puzzle(wpId: $wpId) {
      id
      wpId
      title
      brand
      pieceCount
      photoThumbUrl
    }
  }
`)

const logJourneyMutation = gql(/* GraphQL */ `
  mutation JourneyLogCreate($input: JourneyLogCreateInput!) {
    journeyLogCreate(input: $input) {
      journeyLog {
        id
      }
    }
  }
`)

export const LogJourneyScreen = () => {
  const { wpId } = useParams<{ wpId: string }>()
  const navigate = useNavigate()
  const { viewer } = useViewer()

  const { data, loading, error } = useQuery(puzzleQuery, {
    variables: { wpId: wpId ?? "" },
    skip: !wpId,
  })
  const puzzle = data?.puzzle

  useDocumentTitle(puzzle ? `Log your journey · ${puzzle.title}` : null)

  const [exec, { loading: submitting }] = useMutation(logJourneyMutation)

  if (loading) return <ScreenLoading />
  if (error) return <GraphqlError error={error} />
  if (!puzzle || !wpId) {
    return (
      <main className="mx-auto max-w-2xl px-4 py-12">
        <h1 className="text-2xl font-semibold">Puzzle not found</h1>
        <Link to="/" className="mt-4 inline-block text-blue-600 underline">
          Back to home
        </Link>
      </main>
    )
  }

  return (
    <main className="mx-auto max-w-2xl px-4 py-6 sm:py-10">
      <Link to={`/p/${puzzle.wpId}`} className="text-sm text-gray-500 hover:text-gray-700">
        ← Back to puzzle
      </Link>

      <Card className="mt-4">
        <CardContent className="flex items-center gap-4 py-4">
          {puzzle.photoThumbUrl ? (
            <img
              src={puzzle.photoThumbUrl}
              alt={puzzle.title}
              className="h-16 w-16 rounded bg-stone-100 object-contain"
            />
          ) : null}
          <div>
            <div className="text-xs uppercase tracking-wider text-gray-500">Logging a stop on</div>
            <div className="font-medium">{puzzle.title}</div>
            <div className="text-sm text-gray-500">
              {puzzle.brand} · {puzzle.pieceCount.toLocaleString()} pieces
            </div>
          </div>
        </CardContent>
      </Card>

      <JourneyLogForm
        defaultValues={{
          completedOn: new Date().toISOString().slice(0, 10),
          city: "",
          state: "",
          zipCode: "",
          comment: "",
          passedOn: false,
          nextLocation: "",
          displayAs: viewer.defaultDisplayAs ?? "FIRST_NAME",
        }}
        firstName={viewer.firstName}
        submitLabel="Submit log"
        submittingLabel="Saving…"
        submitting={submitting}
        cancelTo={`/p/${puzzle.wpId}`}
        onSubmit={async ({ values, photoFile }) => {
          let photoSignedId: string | undefined
          if (photoFile) {
            try {
              const upload = await directImageUpload(photoFile)
              photoSignedId = upload.signedId
            } catch {
              toast.error("Photo upload failed", {
                description: "Try a smaller image or skip the photo.",
              })
              return
            }
          }

          const result = await exec({
            variables: {
              input: {
                wpId,
                journeyLogInput: {
                  completedOn: values.completedOn,
                  city: values.city?.trim() || null,
                  state: values.state?.trim() || null,
                  zipCode: values.zipCode?.trim() || null,
                  comment: values.comment?.trim() || null,
                  passedOn: values.passedOn,
                  nextLocation: values.passedOn ? values.nextLocation?.trim() || null : null,
                  displayAs: values.displayAs as JourneyLogDisplay,
                },
                photoSignedId,
              },
            },
          })

          if (result.error) {
            toast.error("Could not save your log", {
              description: "Please check the form and try again.",
            })
            return
          }

          toast.success("Logged!", { description: "Thanks for adding your stop." })
          navigate(`/p/${wpId}`)
        }}
      />
    </main>
  )
}
